/**
 * 微信扫码回调页。路由 #auth/wechat，微信会带 ?code=..&state=.. 回到这里。
 *
 * 注意 hash 路由和查询串的关系：微信把参数拼在 `?` 后、`#` 前，
 * 也就是 https://tengyunzi.cn/?code=..&state=..#auth/wechat，
 * 所以要从 location.search 读，不是从 hash 读。
 */
import { useEffect, useRef, useState } from "react";
import { callApi, setSession, type Session } from "../api";

export function WechatCallbackPage({ onNavigate }: { onNavigate: (route: string) => void }) {
  const [message, setMessage] = useState("正在完成微信登录…");
  const [failed, setFailed] = useState(false);
  // React 18+ StrictMode 会跑两次 effect，而 code 只能用一次，用 ref 挡住第二次
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      setFailed(true);
      setMessage("回调参数不完整，请重新扫码登录。");
      return;
    }

    callApi<{ session?: Session; returnTo?: string }>("auth.wechat.callback", { code, state }).then(
      (result) => {
        if (!result.ok || !result.session?.token) {
          setFailed(true);
          setMessage(result.ok ? "微信登录返回缺少会话信息。" : result.error);
          return;
        }
        setSession(result.session);
        // 清掉地址栏里的 code/state，避免刷新时重复提交
        window.history.replaceState(null, "", window.location.pathname);
        onNavigate(result.returnTo || "records");
      },
    );
  }, [onNavigate]);

  return (
    <main className="page auth-page">
      <h1>微信登录</h1>
      <p className={failed ? "form-error" : "empty-state"}>{message}</p>
      {failed && (
        <button type="button" onClick={() => onNavigate("auth")}>
          返回登录页
        </button>
      )}
    </main>
  );
}
